"use client";

/**
 * Screen 4 — Conversion Gate.
 * Inline card (not modal). Two variants: standard and softened (after 👎).
 * Stashes current prompt in localStorage before Google OAuth redirect
 * so Dashboard can restore it.
 */
import { useState } from "react";
import { requestMagicLink, googleAuthUrl } from "@/lib/web-client";

interface ConversionGateProps {
  softened?: boolean;
  /** If provided, stashed in localStorage before Google redirect */
  pendingPrompt?: string;
  /** If provided, renders a "Maybe later" dismiss control */
  onDismiss?: () => void;
}

export default function ConversionGate({
  softened = false,
  pendingPrompt,
  onDismiss,
}: ConversionGateProps) {
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);

  const handleGoogle = () => {
    if (pendingPrompt) {
      localStorage.setItem("bw_pending_prompt", pendingPrompt);
    }
    window.location.href = googleAuthUrl();
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    setEmailError(null);
    try {
      const res = await requestMagicLink(email.trim());
      if (res.error === "email_not_configured") {
        setEmailError("Email is not configured on this server. Please use Google to sign in.");
      } else {
        if (pendingPrompt) {
          localStorage.setItem("bw_pending_prompt", pendingPrompt);
        }
        setEmailSent(true);
      }
    } catch {
      setEmailError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 16,
        padding: "28px 24px",
        marginTop: 24,
      }}
    >
      {/* Header */}
      <h3
        style={{
          fontFamily:
            "var(--font-plus-jakarta), 'Plus Jakarta Sans', sans-serif",
          fontWeight: 700,
          fontSize: 18,
          color: "#F2F0ED",
          marginBottom: 8,
          letterSpacing: "-0.3px",
        }}
      >
        {softened ? "Let's get you a better result" : "Run another task — free"}
      </h3>

      <p
        style={{
          fontFamily: "var(--font-plus-jakarta), sans-serif",
          fontSize: 14,
          color: "rgba(205,194,216,0.65)",
          lineHeight: 1.6,
          marginBottom: 20,
        }}
      >
        {softened
          ? "Your next run is on us. Create a free account and we'll re-run this (or a new task) — plus $10 in credit to keep going."
          : "Create a free account to keep your task history and get $10 in credit. Takes one tap."}
      </p>

      {emailSent ? (
        <div
          style={{
            padding: "14px 16px",
            background: "rgba(74,222,128,0.05)",
            border: "1px solid rgba(74,222,128,0.15)",
            borderRadius: 10,
            fontSize: 14,
            color: "rgba(74,222,128,0.85)",
            fontFamily: "var(--font-plus-jakarta), sans-serif",
          }}
        >
          Check your email — we sent you a sign-in link.
        </div>
      ) : emailMode ? (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              autoFocus
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleEmailSubmit();
              }}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 9,
                padding: "11px 14px",
                color: "#F2F0ED",
                fontSize: 14,
                fontFamily: "var(--font-plus-jakarta), sans-serif",
                outline: "none",
              }}
            />
            <button
              onClick={() => void handleEmailSubmit()}
              disabled={submitting || !email.trim()}
              style={{
                background:
                  submitting || !email.trim()
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(134,53,246,0.2)",
                border: "1px solid rgba(134,53,246,0.3)",
                borderRadius: 9,
                padding: "11px 18px",
                color:
                  submitting || !email.trim()
                    ? "rgba(255,255,255,0.3)"
                    : "#D5BAFF",
                fontFamily: "var(--font-plus-jakarta), sans-serif",
                fontWeight: 700,
                fontSize: 14,
                cursor:
                  submitting || !email.trim() ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Sending…" : "Send link"}
            </button>
          </div>
          {emailError && (
            <p
              style={{
                fontSize: 13,
                color: "#FCA5A5",
                fontFamily: "var(--font-plus-jakarta), sans-serif",
                marginBottom: 8,
              }}
            >
              {emailError}
            </p>
          )}
          <button
            onClick={() => setEmailMode(false)}
            style={{
              background: "none",
              border: "none",
              color: "rgba(205,194,216,0.4)",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              padding: 0,
            }}
          >
            ← Back
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Google CTA */}
          <button
            onClick={handleGoogle}
            style={{
              background: "linear-gradient(135deg, #8635F6 0%, #6B28D6 100%)",
              border: "none",
              borderRadius: 10,
              padding: "12px 24px",
              color: "#fff",
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: "0 4px 16px rgba(134,53,246,0.3)",
              transition: "all 150ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 6px 24px rgba(134,53,246,0.45)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                "0 4px 16px rgba(134,53,246,0.3)";
              e.currentTarget.style.transform = "";
            }}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          {/* Email CTA */}
          <button
            onClick={() => setEmailMode(true)}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 10,
              padding: "11px 24px",
              color: "rgba(242,240,237,0.75)",
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              width: "100%",
              transition: "all 150ms ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.07)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.04)")
            }
          >
            Use email instead
          </button>
        </div>
      )}

      {/* Dismiss */}
      {!emailSent && onDismiss && (
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              color: "rgba(205,194,216,0.35)",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              padding: 0,
            }}
          >
            Maybe later
          </button>
        </div>
      )}

      {/* Why an account? */}
      {!emailSent && (
        <div style={{ marginTop: 14 }}>
          <button
            onClick={() => setWhyOpen((o) => !o)}
            style={{
              display: "block",
              margin: "0 auto",
              background: "none",
              border: "none",
              color: "rgba(205,194,216,0.35)",
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              padding: 0,
            }}
          >
            Why an account? {whyOpen ? "▲" : "▼"}
          </button>
          {whyOpen && (
            <p
              style={{
                marginTop: 8,
                textAlign: "center",
                fontSize: 12,
                color: "rgba(205,194,216,0.45)",
                fontFamily: "var(--font-plus-jakarta), sans-serif",
                lineHeight: 1.6,
              }}
            >
              So your tasks and results are saved to you, and so we can give
              you the $10 free credit. No card needed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
